import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { useSiteData } from '@/hooks/useAuditData';
import { useRecommendationTracking, RecommendationStatus } from '@/hooks/useRecommendationTracking';
import { getScoreColor, formatMetricValue } from '@/lib/utils';
import { getPercentileRank, getPercentileDescription, getPercentileColor, RESTAURANT_BENCHMARKS } from '@/lib/benchmarks';
import { PriorityMatrix } from '@/components/visualizations/PriorityMatrix';
import { BenchmarkComparison } from '@/components/visualizations/BenchmarkComparison';
import { ConversionFunnel } from '@/components/visualizations/ConversionFunnel';
import { EventsCateringPanel } from '@/components/visualizations/EventsCateringPanel';
import { SiteArchitecturePanel } from '@/components/site-detail/SiteArchitecturePanel';
import {
  type TabId,
  type SubPanelId,
  ScoreBlock,
  SubPanelNav,
  MetricCardWithAI,
  CheckRow,
  SchemaField,
  QualityScoreCard,
  TrackingCard,
  PageDetailCard,
  PageTrackingCard,
  ElementTrackingOverview,
  TrackingMethodsChart,
  UntrackedElementsSummary,
  WhatSuccessLooksLike,
} from '@/components/site-detail';
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Download,
  Printer,
  LayoutGrid,
  MessageSquare,
  Clock,
  Smartphone,
  Monitor,
  Gauge,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  Lightbulb,
  Zap,
  Eye,
  Search,
  BarChart3,
  Network,
  MapPin,
  Store,
  Code,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export function SiteDetail() {
  const { siteId } = useParams<{ siteId: string }>();
  const { site, loading, error } = useSiteData(siteId);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [activeSubPanel, setActiveSubPanel] = useState<SubPanelId>('summary');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground mb-4">Site not found</p>
        <Link to="/" className="text-sm underline">Back to overview</Link>
      </div>
    );
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'vitals', label: 'Website Vitals' },
    { id: 'seo', label: 'SEO' },
    { id: 'tracking', label: 'Tracking' },
  ];

  return (
    <div className="max-w-6xl mx-auto py-8">
      {/* Back Link */}
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        All Sites
      </Link>

      {/* Header */}
      <header className="mb-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-light">{site.name}</h1>
            <a
              href={site.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mt-1 text-sm"
            >
              {site.url}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="flex items-start gap-6">
            <div className="flex gap-6 text-right">
              <ScoreBlock label="Performance" score={site.vitals.performanceScore} />
              <ScoreBlock label="SEO" score={site.seo.score} />
              <ScoreBlock label="Accessibility" score={site.vitals.accessibilityScore} />
            </div>
            <div className="flex gap-2 no-print">
              <button
                onClick={() => {
                  const blob = new Blob([JSON.stringify(site, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${site.id}-audit-${new Date().toISOString().split('T')[0]}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="p-2 text-sm bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
                title="Export JSON"
              >
                <Download className="h-4 w-4" />
              </button>
              <button
                onClick={() => window.print()}
                className="p-2 text-sm bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
                title="Print / PDF"
              >
                <Printer className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Tabs */}
      <div className="border-b border-border mb-6">
        <nav className="flex gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setActiveSubPanel('summary');
              }}
              className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab site={site} />}
      {activeTab === 'vitals' && (
        <VitalsTab site={site} activePanel={activeSubPanel} setActivePanel={setActiveSubPanel} />
      )}
      {activeTab === 'seo' && (
        <SeoTab site={site} activePanel={activeSubPanel} setActivePanel={setActiveSubPanel} />
      )}
      {activeTab === 'tracking' && (
        <TrackingTab site={site} activePanel={activeSubPanel} setActivePanel={setActiveSubPanel} />
      )}
    </div>
  );
}

// ============================================
// Overview Tab
// ============================================

function OverviewTab({ site }: { site: any }) {
  const [priorityFilter, setPriorityFilter] = useState<number | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<RecommendationStatus | 'all'>('all');
  const [showMatrix, setShowMatrix] = useState(false);
  const [showTracking, setShowTracking] = useState(false);

  const { getTracking, updateTracking, getStats } = useRecommendationTracking(site.id);
  const stats = getStats();

  const categories = Array.from(new Set(site.recommendations.map((r: any) => r.category))) as string[];

  const filteredRecommendations = site.recommendations.filter((r: any) => {
    if (priorityFilter !== 'all' && r.priority !== priorityFilter) return false;
    if (categoryFilter !== 'all' && r.category !== categoryFilter) return false;
    if (statusFilter !== 'all') {
      const tracking = getTracking(r.id);
      const recStatus = tracking?.status || 'pending';
      if (recStatus !== statusFilter) return false;
    }
    return true;
  });

  // Group recommendations by priority for a structured flow
  const groupedRecs = {
    critical: filteredRecommendations.filter((r: any) => r.priority === 1).sort((a: any, b: any) => a.priority - b.priority),
    high: filteredRecommendations.filter((r: any) => r.priority === 2),
    other: filteredRecommendations.filter((r: any) => r.priority >= 3),
  };

  return (
    <div className="space-y-8">
      {/* Why This Matters - Overview */}
      {(() => {
        const { headline, bullets } = generateWhyItMatters('overview', site);
        return <WhyItMatters headline={headline} bullets={bullets} segment="overview" />;
      })()}

      {/* What Success Looks Like - Overview */}
      <WhatSuccessLooksLike segment="overview" site={site} />

      {/* Priority Matrix Visualization */}
      {showMatrix && (
        <section className="space-y-4">
          <PriorityMatrix recommendations={site.recommendations} siteName={site.name} />
        </section>
      )}

      {/* Toolbar — compact, muted */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {filteredRecommendations.length} recommendation{filteredRecommendations.length === 1 ? '' : 's'}
          </span>
          <span className="text-border">|</span>
          <div className="flex gap-1 no-print">
            {[
              { val: 'all' as const, label: 'All' },
              { val: 1 as const, label: 'Critical' },
              { val: 2 as const, label: 'High' },
              { val: 3 as const, label: 'Medium' },
            ].map(({ val, label }) => (
              <button
                key={String(val)}
                onClick={() => setPriorityFilter(val)}
                className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                  priorityFilter === val
                    ? 'bg-foreground/10 text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {categories.length > 1 && (
            <>
              <span className="text-border">|</span>
              <div className="flex gap-1 no-print">
                <button
                  onClick={() => setCategoryFilter('all')}
                  className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                    categoryFilter === 'all' ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  All
                </button>
                {categories.map((cat: string) => (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(cat)}
                    className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                      categoryFilter === cat ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 no-print">
          <button
            onClick={() => setShowMatrix(!showMatrix)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full transition-colors ${
              showMatrix ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <LayoutGrid className="h-3 w-3" />
            Matrix
          </button>
          <button
            onClick={() => setShowTracking(!showTracking)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full transition-colors ${
              showTracking ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Clock className="h-3 w-3" />
            Track
          </button>
          {showTracking && (
            <div className="flex items-center gap-1 ml-2">
              {(['all', 'pending', 'in_progress', 'fixed'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-2 py-1 text-xs rounded-full transition-colors ${
                    statusFilter === s ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {s === 'all' ? 'All' : s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}
                  {s !== 'all' && <span className="ml-1 text-muted-foreground/40">
                    {s === 'fixed' ? stats.fixed : s === 'in_progress' ? stats.inProgress : stats.pending || ''}
                  </span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recommendations — grouped, clean flow */}
      {filteredRecommendations.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">No recommendations match the current filters.</p>
      ) : (
        <div className="space-y-6">
          {/* Critical — if any */}
          {groupedRecs.critical.length > 0 && (
            <RecommendationGroup
              label="Address First"
              count={groupedRecs.critical.length}
              accent="border-l-red-400/50"
              recommendations={groupedRecs.critical}
              showTracking={showTracking}
              getTracking={getTracking}
              updateTracking={updateTracking}
            />
          )}

          {/* High */}
          {groupedRecs.high.length > 0 && (
            <RecommendationGroup
              label="Important"
              count={groupedRecs.high.length}
              accent="border-l-orange-400/40"
              recommendations={groupedRecs.high}
              showTracking={showTracking}
              getTracking={getTracking}
              updateTracking={updateTracking}
            />
          )}

          {/* Medium & Low */}
          {groupedRecs.other.length > 0 && (
            <RecommendationGroup
              label="Worth Considering"
              count={groupedRecs.other.length}
              accent="border-l-blue-400/30"
              recommendations={groupedRecs.other}
              showTracking={showTracking}
              getTracking={getTracking}
              updateTracking={updateTracking}
            />
          )}
        </div>
      )}
    </div>
  );
}

function RecommendationGroup({
  label,
  count,
  accent,
  recommendations,
  showTracking,
  getTracking,
  updateTracking,
}: {
  label: string;
  count: number;
  accent: string;
  recommendations: any[];
  showTracking: boolean;
  getTracking: (id: string) => any;
  updateTracking: (id: string, updates: any) => void;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</h3>
        <span className="text-xs text-muted-foreground/60">{count}</span>
      </div>
      <div className="space-y-2">
        {recommendations.map((rec: any) => (
          <RecommendationCard
            key={rec.id}
            rec={rec}
            accent={accent}
            showTracking={showTracking}
            getTracking={getTracking}
            updateTracking={updateTracking}
          />
        ))}
      </div>
    </div>
  );
}

function RecommendationCard({
  rec,
  accent,
  showTracking,
  getTracking,
  updateTracking,
}: {
  rec: any;
  accent: string;
  showTracking: boolean;
  getTracking: (id: string) => any;
  updateTracking: (id: string, updates: any) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const tracking = getTracking(rec.id);
  const status = tracking?.status || 'pending';
  const isResolved = status === 'fixed' || status === 'wont_fix';

  return (
    <div className={`rounded-lg border border-border/50 overflow-hidden transition-opacity no-page-break border-l-2 ${accent} ${
      isResolved ? 'opacity-50' : ''
    }`}>
      {/* Clickable header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-secondary/20 transition-colors"
      >
        <ChevronDown className={`h-4 w-4 text-muted-foreground/40 shrink-0 mt-0.5 transition-transform duration-200 ${
          expanded ? 'rotate-180' : ''
        }`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-medium">{rec.title}</h4>
            <span className="text-xs text-muted-foreground/60 uppercase">{rec.category}</span>
            {showTracking && status !== 'pending' && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                status === 'fixed' ? 'bg-green-500/10 text-green-400' :
                status === 'in_progress' ? 'bg-blue-500/10 text-blue-400' :
                'bg-secondary text-muted-foreground'
              }`}>
                {status === 'in_progress' ? 'In Progress' : status === 'wont_fix' ? "Won't Fix" : 'Fixed'}
              </span>
            )}
          </div>
        </div>
        {(rec.impact || rec.effort) && (
          <div className="flex gap-3 shrink-0 text-xs text-muted-foreground/60">
            {rec.impact && <span>{rec.impact} impact</span>}
            {rec.effort && <span>{rec.effort} effort</span>}
          </div>
        )}
      </button>

      {/* Expandable detail */}
      <div className={`overflow-hidden transition-all duration-200 ${
        expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-4 pb-4 pt-1 ml-7">
          <p className="text-sm text-muted-foreground leading-relaxed">{rec.description}</p>
          {rec.businessImpact && (
            <p className="text-sm text-foreground/70 mt-2 italic">{rec.businessImpact}</p>
          )}

          {/* Tracking Controls */}
          {showTracking && (
            <div className="mt-3 pt-3 border-t border-border/30 no-print">
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={status}
                  onChange={(e) => updateTracking(rec.id, { status: e.target.value as RecommendationStatus })}
                  className="text-xs bg-secondary border border-border rounded px-2 py-1"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="fixed">Fixed</option>
                  <option value="wont_fix">Won't Fix</option>
                </select>
                <input
                  type="text"
                  placeholder="Assignee"
                  value={tracking?.assignee || ''}
                  onChange={(e) => updateTracking(rec.id, { assignee: e.target.value || null })}
                  className="text-xs bg-secondary border border-border rounded px-2 py-1 w-24"
                />
                <button
                  onClick={() => setShowNotes(!showNotes)}
                  className={`text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                    tracking?.notes ? 'text-blue-400' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <MessageSquare className="h-3 w-3" />
                  Notes
                </button>
              </div>
              {showNotes && (
                <textarea
                  placeholder="Add notes..."
                  value={tracking?.notes || ''}
                  onChange={(e) => updateTracking(rec.id, { notes: e.target.value || null })}
                  className="mt-2 w-full text-xs bg-secondary border border-border rounded px-2 py-1 h-16 resize-none"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Website Vitals Tab
// ============================================

function VitalsTab({ site, activePanel, setActivePanel }: { site: any; activePanel: SubPanelId; setActivePanel: (id: SubPanelId) => void }) {
  const subPanels = [
    { id: 'summary' as const, label: 'Summary' },
    { id: 'metrics' as const, label: 'Detailed Metrics' },
    { id: 'benchmarks' as const, label: 'Industry Benchmarks' },
  ];

  // Get metric insights from the detailed insights structure
  const metricInsights = site.insights?.vitals?.metrics || {};

  // Calculate benchmark rankings for executive summary
  const perfPercentile = getPercentileRank(site.vitals.performanceScore, RESTAURANT_BENCHMARKS.performance);
  const a11yPercentile = getPercentileRank(site.vitals.accessibilityScore, RESTAURANT_BENCHMARKS.accessibility);
  const bpPercentile = getPercentileRank(site.vitals.bestPracticesScore, RESTAURANT_BENCHMARKS.bestPractices);

  // Load time calculations
  const speedIndex = site.vitals.metrics.speedIndex?.value || 0;
  const mobileLoadTime = speedIndex; // Lighthouse runs mobile by default
  const desktopLoadTime = speedIndex * 0.7; // Desktop typically 30% faster

  const getLoadTimeRating = (time: number): 'good' | 'needs-improvement' | 'poor' => {
    if (time <= 3.4) return 'good';
    if (time <= 5.8) return 'needs-improvement';
    return 'poor';
  };

  return (
    <div className="space-y-6">
      <SubPanelNav panels={subPanels} active={activePanel} onChange={setActivePanel} />

      {activePanel === 'summary' && (
        <div className="space-y-6">
          {/* Executive Summary - Benchmark Rankings */}
          <div className="bg-secondary/20 rounded-lg p-5 border border-border/50">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
              Executive Summary — Industry Ranking
            </h3>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <ExecutiveScoreCard
                label="Performance"
                score={site.vitals.performanceScore}
                percentile={perfPercentile}
              />
              <ExecutiveScoreCard
                label="Accessibility"
                score={site.vitals.accessibilityScore}
                percentile={a11yPercentile}
              />
              <ExecutiveScoreCard
                label="Best Practices"
                score={site.vitals.bestPracticesScore}
                percentile={bpPercentile}
              />
            </div>

            {/* Load Time Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LoadTimeCard
                icon={<Smartphone className="h-5 w-5" />}
                label="Mobile Load Time"
                time={mobileLoadTime}
                rating={getLoadTimeRating(mobileLoadTime)}
                benchmark="Target: < 3.4s"
              />
              <LoadTimeCard
                icon={<Monitor className="h-5 w-5" />}
                label="Desktop Load Time"
                time={desktopLoadTime}
                rating={getLoadTimeRating(desktopLoadTime)}
                benchmark="Target: < 2.5s"
              />
            </div>
          </div>

          {/* Why This Matters - Vitals */}
          {(() => {
            const { headline, bullets } = generateWhyItMatters('vitals', site);
            return <WhyItMatters headline={headline} bullets={bullets} segment="vitals" />;
          })()}

          {/* What Success Looks Like - Vitals */}
          <WhatSuccessLooksLike segment="vitals" site={site} />

          {/* Core Web Vitals Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* LCP Card with explanation */}
            <MetricCardWithAI
              label="LCP"
              fullName="Largest Contentful Paint"
              value={formatMetricValue(site.vitals.metrics.lcp.value, site.vitals.metrics.lcp.unit)}
              rating={site.vitals.metrics.lcp.rating}
              insight={metricInsights.lcp}
              metricKey="lcp"
            />

            {/* CLS Card */}
            <MetricCardWithAI
              label="CLS"
              fullName="Cumulative Layout Shift"
              value={formatMetricValue(site.vitals.metrics.cls.value, site.vitals.metrics.cls.unit)}
              rating={site.vitals.metrics.cls.rating}
              insight={metricInsights.cls}
              metricKey="cls"
            />

            {/* FCP Card */}
            <MetricCardWithAI
              label="FCP"
              fullName="First Contentful Paint"
              value={formatMetricValue(site.vitals.metrics.fcp.value, site.vitals.metrics.fcp.unit)}
              rating={site.vitals.metrics.fcp.rating}
              insight={metricInsights.fcp}
              metricKey="fcp"
            />

            {/* TTFB Card */}
            <MetricCardWithAI
              label="TTFB"
              fullName="Time to First Byte"
              value={formatMetricValue(site.vitals.metrics.ttfb.value, site.vitals.metrics.ttfb.unit)}
              rating={site.vitals.metrics.ttfb.rating}
              insight={metricInsights.ttfb}
              metricKey="ttfb"
            />
          </div>

        </div>
      )}

      {activePanel === 'metrics' && (
        <div className="space-y-4">
          {/* Performance Breakdown */}
          <ScoreBreakdownCard
            title="Performance"
            score={site.vitals.performanceScore}
            description="Measures how quickly your page loads and becomes interactive. Affects user experience and search rankings."
            showMetrics={true}
            factors={[
              {
                name: 'FCP',
                detail: 'First Contentful Paint',
                impact: 'high',
                status: site.vitals.metrics.fcp.rating === 'good' ? 'pass' : site.vitals.metrics.fcp.rating === 'needs-improvement' ? 'warning' : 'fail',
                value: `${site.vitals.metrics.fcp.value}s`,
                target: '< 1.8s'
              },
              {
                name: 'LCP',
                detail: 'Largest Contentful Paint',
                impact: 'high',
                status: site.vitals.metrics.lcp.rating === 'good' ? 'pass' : site.vitals.metrics.lcp.rating === 'needs-improvement' ? 'warning' : 'fail',
                value: `${site.vitals.metrics.lcp.value}s`,
                target: '< 2.5s'
              },
              {
                name: 'TBT',
                detail: 'Total Blocking Time',
                impact: 'high',
                status: site.vitals.performanceScore >= 50 ? 'pass' : 'warning',
                value: site.vitals.performanceScore >= 50 ? '< 200ms' : '> 200ms',
                target: '< 200ms'
              },
              {
                name: 'CLS',
                detail: 'Cumulative Layout Shift',
                impact: 'medium',
                status: site.vitals.metrics.cls.rating === 'good' ? 'pass' : site.vitals.metrics.cls.rating === 'needs-improvement' ? 'warning' : 'fail',
                value: site.vitals.metrics.cls.value.toString(),
                target: '< 0.1'
              },
              {
                name: 'SI',
                detail: 'Speed Index',
                impact: 'medium',
                status: (site.vitals.metrics.speedIndex?.value || 0) <= 3.4 ? 'pass' : (site.vitals.metrics.speedIndex?.value || 0) <= 5.8 ? 'warning' : 'fail',
                value: `${site.vitals.metrics.speedIndex?.value?.toFixed(1) || 'N/A'}s`,
                target: '< 3.4s'
              },
              {
                name: 'TTFB',
                detail: 'Time to First Byte',
                impact: 'medium',
                status: site.vitals.metrics.ttfb.rating === 'good' ? 'pass' : site.vitals.metrics.ttfb.rating === 'needs-improvement' ? 'warning' : 'fail',
                value: `${site.vitals.metrics.ttfb.value}s`,
                target: '< 0.8s'
              },
            ]}
          />

          {/* Accessibility Breakdown */}
          <ScoreBreakdownCard
            title="Accessibility"
            score={site.vitals.accessibilityScore}
            description="Measures how usable your site is for people with disabilities. Affects legal compliance and audience reach."
            showMetrics={true}
            factors={[
              {
                name: 'Contrast',
                detail: 'Color Contrast Ratio',
                impact: 'high',
                status: site.vitals.accessibilityScore >= 90 ? 'pass' : 'warning',
                value: site.vitals.accessibilityScore >= 90 ? 'Pass' : 'Fail',
                target: 'WCAG AA'
              },
              {
                name: 'Alt Text',
                detail: 'Image Descriptions',
                impact: 'high',
                status: site.vitals.accessibilityScore >= 80 ? 'pass' : 'warning',
                value: site.vitals.accessibilityScore >= 80 ? 'Present' : 'Missing',
                target: 'All images'
              },
              {
                name: 'Labels',
                detail: 'Form Field Labels',
                impact: 'high',
                status: site.vitals.accessibilityScore >= 85 ? 'pass' : 'warning',
                value: site.vitals.accessibilityScore >= 85 ? 'Labeled' : 'Missing',
                target: 'All inputs'
              },
              {
                name: 'Keyboard',
                detail: 'Keyboard Navigation',
                impact: 'medium',
                status: site.vitals.accessibilityScore >= 70 ? 'pass' : 'warning',
                value: site.vitals.accessibilityScore >= 70 ? 'Navigable' : 'Limited',
                target: 'Full access'
              },
              {
                name: 'ARIA',
                detail: 'ARIA Attributes',
                impact: 'medium',
                status: site.vitals.accessibilityScore >= 75 ? 'pass' : 'warning',
                value: site.vitals.accessibilityScore >= 75 ? 'Valid' : 'Issues',
                target: 'Valid usage'
              },
              {
                name: 'Headings',
                detail: 'Heading Hierarchy',
                impact: 'low',
                status: site.seo.headings.h1Count === 1 ? 'pass' : site.seo.headings.h1Count === 0 ? 'fail' : 'warning',
                value: site.seo.headings.h1Count === 1 ? 'Valid H1' : site.seo.headings.h1Count === 0 ? 'Missing H1' : `${site.seo.headings.h1Count} H1s`,
                target: 'Single H1'
              },
            ]}
          />

          {/* Best Practices Breakdown */}
          <ScoreBreakdownCard
            title="Best Practices"
            score={site.vitals.bestPracticesScore}
            description="Measures adherence to modern web development standards. Affects security, reliability, and user trust."
            showMetrics={true}
            factors={[
              {
                name: 'HTTPS',
                detail: 'Secure Connection',
                impact: 'high',
                status: site.url.startsWith('https') ? 'pass' : 'fail',
                value: site.url.startsWith('https') ? 'Enabled' : 'Disabled',
                target: 'Required'
              },
              {
                name: 'Mixed',
                detail: 'No Mixed Content',
                impact: 'high',
                status: site.vitals.bestPracticesScore >= 80 ? 'pass' : 'warning',
                value: site.vitals.bestPracticesScore >= 80 ? 'Clean' : 'Found',
                target: 'None'
              },
              {
                name: 'Errors',
                detail: 'Console Errors',
                impact: 'medium',
                status: site.vitals.bestPracticesScore >= 90 ? 'pass' : 'warning',
                value: site.vitals.bestPracticesScore >= 90 ? 'None' : 'Present',
                target: 'Zero'
              },
              {
                name: 'Images',
                detail: 'Modern Image Formats',
                impact: 'medium',
                status: site.vitals.bestPracticesScore >= 85 ? 'pass' : 'warning',
                value: site.vitals.bestPracticesScore >= 85 ? 'Optimized' : 'Legacy',
                target: 'WebP/AVIF'
              },
              {
                name: 'Maps',
                detail: 'Valid Source Maps',
                impact: 'low',
                status: site.vitals.bestPracticesScore >= 95 ? 'pass' : 'warning',
                value: site.vitals.bestPracticesScore >= 95 ? 'Valid' : 'Issues',
                target: 'Linked'
              },
              {
                name: 'APIs',
                detail: 'No Deprecated APIs',
                impact: 'low',
                status: site.vitals.bestPracticesScore >= 90 ? 'pass' : 'warning',
                value: site.vitals.bestPracticesScore >= 90 ? 'Modern' : 'Outdated',
                target: 'Current'
              },
            ]}
          />
        </div>
      )}

      {activePanel === 'benchmarks' && (
        <BenchmarkComparison
          metrics={{
            performance: site.vitals.performanceScore,
            accessibility: site.vitals.accessibilityScore,
            bestPractices: site.vitals.bestPracticesScore,
            lcp: site.vitals.metrics.lcp.value,
            fcp: site.vitals.metrics.fcp.value,
            cls: site.vitals.metrics.cls.value,
            ttfb: site.vitals.metrics.ttfb.value,
          }}
        />
      )}
    </div>
  );
}

// ============================================
// SEO Tab
// ============================================

function SeoTab({ site, activePanel, setActivePanel }: { site: any; activePanel: SubPanelId; setActivePanel: (id: SubPanelId) => void }) {
  const hasLocalSeo = !!site.seo.localSeo;
  const hasEventsCatering = !!site.eventsCatering;
  const hasArchitecture = !!site.architecture;
  const seoContext = site.seo.context;
  const hasSeoContext = !!seoContext && (
    !!seoContext.brand ||
    (seoContext.locations?.length || 0) > 0 ||
    (seoContext.cuisines?.length || 0) > 0 ||
    (seoContext.offerings?.length || 0) > 0 ||
    (seoContext.serviceTypes?.length || 0) > 0 ||
    (seoContext.keywords?.length || 0) > 0
  );

  const subPanels = [
    { id: 'summary' as const, label: 'Summary' },
    { id: 'metrics' as const, label: `Pages & Quality (${site.seo.pages?.length || 1})` },
    ...(hasArchitecture ? [{ id: 'architecture' as const, label: 'Site Architecture' }] : []),
    ...(hasLocalSeo ? [{ id: 'local' as const, label: 'Local SEO' }] : []),
    ...(hasEventsCatering ? [{ id: 'catering' as const, label: 'Events & Catering' }] : []),
  ];

  return (
    <div className="space-y-6">
      <SubPanelNav panels={subPanels} active={activePanel} onChange={setActivePanel} />

      {activePanel === 'summary' && (
        <div className="space-y-6">
          {/* High-Level Overview */}
          {(() => {
            const pages = site.seo.pages || [];
            const totalPages = pages.length || 1;
            const avgQuality = totalPages > 0
              ? Math.round(pages.reduce((sum: number, p: any) => sum + (p.qualityAnalysis?.overallScore || 0), 0) / totalPages)
              : 0;
            const issueCount = site.seo.issues?.length || 0;

            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-secondary/30 rounded-lg p-4 text-center">
                  <p className={`text-3xl font-light ${getScoreColor(site.seo.score)}`}>{site.seo.score}</p>
                  <p className="text-xs text-muted-foreground mt-1">SEO Score</p>
                </div>
                <div className="bg-secondary/30 rounded-lg p-4 text-center">
                  <p className={`text-3xl font-light ${getScoreColor(avgQuality)}`}>{avgQuality}</p>
                  <p className="text-xs text-muted-foreground mt-1">Avg Page Quality</p>
                </div>
                <div className="bg-secondary/30 rounded-lg p-4 text-center">
                  <p className="text-3xl font-light">{totalPages}</p>
                  <p className="text-xs text-muted-foreground mt-1">Pages Crawled</p>
                </div>
                <div className="bg-secondary/30 rounded-lg p-4 text-center">
                  <p className={`text-3xl font-light ${issueCount > 0 ? 'text-orange-400' : 'text-green-500'}`}>{issueCount}</p>
                  <p className="text-xs text-muted-foreground mt-1">Issues Found</p>
                </div>
              </div>
            );
          })()}

          {/* Why This Matters - SEO (contextualizes the scores above) */}
          {(() => {
            const { headline, bullets } = generateWhyItMatters('seo', site);
            return <WhyItMatters headline={headline} bullets={bullets} segment="seo" />;
          })()}

          {/* What Success Looks Like - SEO */}
          <WhatSuccessLooksLike segment="seo" site={site} />

          {/* Content Coverage */}
          {(() => {
            const pages = site.seo.pages || [];
            const totalPages = pages.length || 1;
            const pagesWithTitle = pages.filter((p: any) => p.title).length;
            const pagesWithDesc = pages.filter((p: any) => p.metaDescription).length;
            const pagesWithH1 = pages.filter((p: any) => p.h1Tags?.length > 0).length;
            const duplicateTitles = site.seo.duplicates?.titles?.length || 0;
            const duplicateDescs = site.seo.duplicates?.descriptions?.length || 0;

            return (
              <div className="bg-secondary/30 rounded-lg p-5">
                <h3 className="text-sm font-medium mb-4">Content Coverage</h3>
                <div className="space-y-3">
                  <CoverageBar label="Pages with Title" count={pagesWithTitle} total={totalPages} issues={duplicateTitles > 0 ? `${duplicateTitles} duplicates` : undefined} />
                  <CoverageBar label="Pages with Meta Description" count={pagesWithDesc} total={totalPages} issues={duplicateDescs > 0 ? `${duplicateDescs} duplicates` : undefined} />
                  <CoverageBar label="Pages with H1 Heading" count={pagesWithH1} total={totalPages} />
                </div>
              </div>
            );
          })()}

          {/* Quality Score Breakdown — aggregated across all pages */}
          {(() => {
            const pages = (site.seo.pages || []).filter((p: any) => p.qualityAnalysis);
            const total = pages.length;
            if (total === 0) return null;

            const avgTitle = Math.round(pages.reduce((s: number, p: any) => s + (p.qualityAnalysis.title?.score || 0), 0) / total);
            const avgDesc = Math.round(pages.reduce((s: number, p: any) => s + (p.qualityAnalysis.description?.score || 0), 0) / total);
            const avgHeadings = Math.round(pages.reduce((s: number, p: any) => s + (p.qualityAnalysis.headings?.score || 0), 0) / total);

            const titleKeywords = pages.filter((p: any) => p.qualityAnalysis.title?.hasKeywords).length;
            const titleLocation = pages.filter((p: any) => p.qualityAnalysis.title?.hasLocation).length;
            const titleNotGeneric = pages.filter((p: any) => !p.qualityAnalysis.title?.isGeneric).length;

            const descKeywords = pages.filter((p: any) => p.qualityAnalysis.description?.hasKeywords).length;
            const descCta = pages.filter((p: any) => p.qualityAnalysis.description?.hasCallToAction).length;
            const descNotGeneric = pages.filter((p: any) => !p.qualityAnalysis.description?.isGeneric).length;

            const headH1Relevant = pages.filter((p: any) => p.qualityAnalysis.headings?.h1IsRelevant).length;
            const headH2Coverage = pages.filter((p: any) => (p.qualityAnalysis.headings?.h2Coverage?.length || 0) >= 2).length;

            return (
              <div className="grid grid-cols-3 gap-4">
                <QualityScoreCard
                  label="Title Quality"
                  score={avgTitle}
                  details={[
                    { label: 'Has Keywords', value: titleKeywords === total, count: titleKeywords, total },
                    { label: 'Has Location', value: titleLocation === total, count: titleLocation, total },
                    { label: 'Not Generic', value: titleNotGeneric === total, count: titleNotGeneric, total },
                  ]}
                />
                <QualityScoreCard
                  label="Description Quality"
                  score={avgDesc}
                  details={[
                    { label: 'Has Keywords', value: descKeywords === total, count: descKeywords, total },
                    { label: 'Has Call-to-Action', value: descCta === total, count: descCta, total },
                    { label: 'Not Generic', value: descNotGeneric === total, count: descNotGeneric, total },
                  ]}
                />
                <QualityScoreCard
                  label="Heading Quality"
                  score={avgHeadings}
                  details={[
                    { label: 'H1 Is Relevant', value: headH1Relevant === total, count: headH1Relevant, total },
                    { label: 'H2 Coverage', value: headH2Coverage === total, count: headH2Coverage, total },
                  ]}
                />
              </div>
            );
          })()}

          {/* SEO Context */}
          <div className="bg-secondary/30 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium">SEO Context</h3>
                <p className="text-xs text-muted-foreground">
                  Dynamic keywords and locations driving the SEO score
                </p>
              </div>
              {hasSeoContext ? (
                <Badge variant="secondary" className="text-xs">Active</Badge>
              ) : (
                <Badge variant="outline" className="text-xs">Pending</Badge>
              )}
            </div>

            {hasSeoContext ? (
              <div className="space-y-3">
                <ContextRow label="Brand" values={seoContext?.brand ? [seoContext.brand] : []} />
                <ContextRow label="Locations" values={seoContext?.locations} />
                <ContextRow label="Cuisines" values={seoContext?.cuisines} />
                <ContextRow label="Offerings" values={seoContext?.offerings} />
                <ContextRow label="Service Types" values={seoContext?.serviceTypes} />
                <ContextRow label="Keywords" values={seoContext?.keywords} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Context will be auto-generated on next audit run.
              </p>
            )}
          </div>

          {/* Technical SEO */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">Technical SEO</h3>
            <div className="bg-secondary/30 rounded-lg p-4 space-y-2">
              <CheckRow label="HTTPS Secure" value={site.seo.technical.hasHttps} />
              <CheckRow label="Mobile Friendly" value={site.seo.technical.isMobileFriendly} />
              <CheckRow label="XML Sitemap" value={site.seo.technical.hasSitemap} />
              <CheckRow label="robots.txt" value={site.seo.technical.hasRobotsTxt} />
              <CheckRow label="Single H1 Heading" value={site.seo.headings.h1Count === 1} />
            </div>
          </div>

        </div>
      )}

      {activePanel === 'metrics' && site.seo.pages && (
        <SeoMetricsPanel pages={site.seo.pages} />
      )}


      {activePanel === 'local' && site.seo.localSeo && (
        <LocalSeoPanel localSeo={site.seo.localSeo} />
      )}

      {activePanel === 'architecture' && site.architecture && (
        <div className="space-y-6">
          {(() => {
            const { headline, bullets } = generateWhyItMatters('architecture', site);
            return <WhyItMatters headline={headline} bullets={bullets} segment="architecture" />;
          })()}
          <SiteArchitecturePanel
            architecture={site.architecture}
            sitemap={site.seo.sitemap}
            pages={site.seo.pages}
          />
        </div>
      )}

      {activePanel === 'catering' && site.eventsCatering && (
        <EventsCateringPanel data={site.eventsCatering} />
      )}
    </div>
  );
}

function ContextRow({ label, values }: { label: string; values?: string[] }) {
  const items = (values || []).filter(Boolean).slice(0, 12);
  return (
    <div className="flex items-start gap-3">
      <div className="w-28 text-xs text-muted-foreground uppercase tracking-wider mt-1">{label}</div>
      {items.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {items.map((value, index) => (
            <Badge key={`${label}-${value}-${index}`} variant="secondary" className="text-xs">
              {value}
            </Badge>
          ))}
        </div>
      ) : (
        <span className="text-sm text-muted-foreground">Not detected</span>
      )}
    </div>
  );
}

function WhyItMatters({ headline, bullets, segment }: { headline: string; bullets: string[]; segment?: 'overview' | 'vitals' | 'seo' | 'tracking' | 'architecture' }) {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  if (!headline && bullets.length === 0) return null;

  // Parse bullets into label + detail pairs
  const parsedBullets = bullets.map((b) => {
    // Try colon split (e.g. "Lost Customers: your site loads slowly...")
    const colonIdx = b.indexOf(':');
    if (colonIdx > 0 && colonIdx < 50) {
      return { label: b.slice(0, colonIdx).trim(), detail: b.slice(colonIdx + 1).trim() };
    }
    // Try first sentence as label (period with remaining text after it)
    const dotIdx = b.indexOf('.');
    if (dotIdx > 10 && dotIdx < 120 && b.length > dotIdx + 5) {
      return { label: b.slice(0, dotIdx + 1).trim(), detail: b.slice(dotIdx + 1).trim() };
    }
    // Long single sentence — split at word boundary for expandable preview
    if (b.length > 80) {
      const breakPoint = b.lastIndexOf(' ', 70);
      if (breakPoint > 20) {
        return { label: b.slice(0, breakPoint).trim() + '...', detail: b };
      }
    }
    return { label: '', detail: b };
  });

  const segmentIcon = segment === 'vitals' ? <Zap className="h-5 w-5" />
    : segment === 'seo' ? <Search className="h-5 w-5" />
    : segment === 'tracking' ? <BarChart3 className="h-5 w-5" />
    : segment === 'architecture' ? <Network className="h-5 w-5" />
    : <Eye className="h-5 w-5" />;

  const segmentLabel = segment === 'vitals' ? 'Website Speed & Experience'
    : segment === 'seo' ? 'Search Visibility'
    : segment === 'tracking' ? 'Marketing & Tracking'
    : segment === 'architecture' ? 'Site Structure & Navigation'
    : 'Overall Health';

  return (
    <div className="rounded-xl border border-border/60 overflow-hidden">
      {/* Header */}
      <div className="bg-secondary/20 px-6 py-5">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-secondary/40 rounded-lg text-muted-foreground shrink-0 mt-0.5">
            {segmentIcon}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{segmentLabel}</span>
              <Lightbulb className="h-3 w-3 text-muted-foreground/40" />
            </div>
            <p className="text-base font-medium text-foreground leading-relaxed">{headline}</p>
          </div>
        </div>
      </div>

      {/* Insight cards */}
      {parsedBullets.length > 0 && (
        <div className="divide-y divide-border/40">
          {parsedBullets.map((bullet, i) => {
            const isExpanded = expandedCard === i;
            const hasDetail = bullet.detail.length > 0;
            const displayLabel = bullet.label || bullet.detail.slice(0, 50) + (bullet.detail.length > 50 ? '...' : '');
            const displayDetail = bullet.label ? bullet.detail : '';

            return (
              <div key={i} className="group">
                <button
                  onClick={() => hasDetail && bullet.label ? setExpandedCard(isExpanded ? null : i) : undefined}
                  className={`w-full text-left px-6 py-3.5 flex items-start gap-3 transition-colors ${
                    hasDetail && bullet.label ? 'hover:bg-secondary/30 cursor-pointer' : 'cursor-default'
                  }`}
                >
                  <span className={`shrink-0 mt-1 h-1.5 w-1.5 rounded-full ${
                    i === 0 ? 'bg-foreground/50' : i === 1 ? 'bg-foreground/35' : i === 2 ? 'bg-foreground/25' : 'bg-foreground/15'
                  }`} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm ${bullet.label ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                      {displayLabel}
                    </p>
                    {/* Show detail inline if no label, or when expanded */}
                    {displayDetail && (
                      <div className={`overflow-hidden transition-all duration-200 ${
                        isExpanded ? 'max-h-40 opacity-100 mt-1.5' : 'max-h-0 opacity-0'
                      }`}>
                        <p className="text-sm text-muted-foreground leading-relaxed">{displayDetail}</p>
                      </div>
                    )}
                  </div>
                  {hasDetail && bullet.label && (
                    <ChevronDown className={`h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5 transition-transform duration-200 ${
                      isExpanded ? 'rotate-180' : ''
                    }`} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Generate business-language insights for each segment.
 * Uses generated insights when available (from Qwen 2.5 via CLI),
 * falls back to programmatic generation from the raw data.
 */
function generateWhyItMatters(
  segment: 'overview' | 'vitals' | 'seo' | 'tracking' | 'architecture',
  site: any
): { headline: string; bullets: string[] } {
  // Use generated insights if available
  const aiInsight = site.insights?.whyItMatters?.[segment];
  if (aiInsight?.headline) {
    return {
      headline: aiInsight.headline,
      bullets: aiInsight.bullets || [],
    };
  }

  // Fallback: programmatic generation from data
  const bullets: string[] = [];

  if (segment === 'overview') {
    const perfScore = site.vitals?.performanceScore ?? 0;
    const seoScore = site.seo?.score ?? 0;
    const issueCount = (site.seo?.issues?.length || 0) + (site.tracking?.issues?.length || 0);
    const recCount = site.recommendations?.length || 0;
    const highPriority = site.recommendations?.filter((r: any) => r.priority <= 2).length || 0;

    const weakest = [
      { label: 'website speed', score: perfScore },
      { label: 'search visibility (SEO)', score: seoScore },
      { label: 'accessibility', score: site.vitals?.accessibilityScore ?? 0 },
    ].sort((a, b) => a.score - b.score)[0];

    const headline = perfScore >= 80 && seoScore >= 80
      ? `Your website is performing well overall, but there ${recCount === 1 ? 'is' : 'are'} still ${recCount} improvement${recCount === 1 ? '' : 's'} that could drive more customers.`
      : `Your website has ${issueCount} issue${issueCount === 1 ? '' : 's'} that may be costing you customers. The biggest opportunity is improving ${weakest.label} (currently scoring ${weakest.score}/100).`;

    if (perfScore < 70) bullets.push(`A slow website loses roughly 1 in 4 visitors before they even see your menu or hours. Your speed score is ${perfScore}/100.`);
    if (seoScore < 70) bullets.push(`When people search for restaurants in your area, a low SEO score (${seoScore}/100) means competitors show up first.`);
    if (highPriority > 0) bullets.push(`${highPriority} high-priority recommendation${highPriority === 1 ? '' : 's'} could have the biggest impact on bringing in more guests.`);
    if (issueCount === 0 && recCount === 0) bullets.push('No critical issues found — your site is in good shape relative to the industry.');

    return { headline, bullets };
  }

  if (segment === 'vitals') {
    const perf = site.vitals?.performanceScore ?? 0;
    const a11y = site.vitals?.accessibilityScore ?? 0;
    const lcp = site.vitals?.metrics?.lcp?.value ?? 0;
    const cls = site.vitals?.metrics?.cls?.value ?? 0;
    const speedIndex = site.vitals?.metrics?.speedIndex?.value ?? 0;

    const headline = perf >= 80
      ? `Your website loads quickly for most visitors. Here's what the numbers mean for your business.`
      : perf >= 50
        ? `Your website takes longer to load than ideal, which means some potential customers are leaving before they see anything.`
        : `Your website is significantly slower than competitors. Studies show ${Math.round(53 - perf * 0.2)}% of mobile users leave sites that take more than 3 seconds to load.`;

    if (lcp > 2.5) bullets.push(`Your main content takes ${lcp.toFixed(1)}s to appear. Visitors expect to see something within 2.5 seconds — every extra second increases the chance they'll leave.`);
    else if (lcp > 0) bullets.push(`Your main content appears in ${lcp.toFixed(1)}s — that's within the target of 2.5s, meaning visitors see your page quickly.`);

    if (cls > 0.1) bullets.push(`Elements on your page shift around as it loads (layout shift score: ${cls.toFixed(2)}). This makes buttons and links hard to tap, frustrating visitors on phones.`);

    if (a11y < 80) bullets.push(`Your accessibility score is ${a11y}/100. This means some visitors using screen readers, voice control, or other assistive technology may struggle to use your site — and it can also affect search rankings.`);
    else if (a11y >= 90) bullets.push(`Strong accessibility score (${a11y}/100) — your site works well for visitors with disabilities, which also helps search engine rankings.`);

    if (speedIndex > 5) bullets.push(`Your site takes ${(speedIndex / 1000).toFixed(1)}s to visually load. On mobile, this means guests searching "restaurants near me" may tap the back button before your page finishes loading.`);

    return { headline, bullets };
  }

  if (segment === 'seo') {
    const score = site.seo?.score ?? 0;
    const pages = site.seo?.pages || [];
    const issueCount = site.seo?.issues?.length || 0;
    const missingTitles = pages.filter((p: any) => !p.title).length;
    const missingDesc = pages.filter((p: any) => !p.metaDescription).length;
    const hasSitemap = site.seo?.technical?.hasSitemap;
    const hasRobots = site.seo?.technical?.hasRobotsTxt;

    const headline = score >= 85
      ? `Your site is well-optimized for search engines. People searching for restaurants like yours are likely to find you.`
      : score >= 60
        ? `Your search visibility has room for improvement. With a score of ${score}/100, you're likely missing out on potential guests who search online before choosing where to eat.`
        : `Your search visibility score is ${score}/100 — this means your competitors are showing up ahead of you when people search for restaurants in your area.`;

    if (missingTitles > 0) bullets.push(`${missingTitles} page${missingTitles === 1 ? ' is' : 's are'} missing a title tag. This is what shows up as the clickable headline in Google — without it, Google guesses, and the result looks unprofessional.`);
    if (missingDesc > 0) bullets.push(`${missingDesc} page${missingDesc === 1 ? ' is' : 's are'} missing a meta description. This is the short preview text in search results that convinces people to click — without it, Google picks random text from your page.`);
    if (!hasSitemap) bullets.push(`No XML sitemap found. A sitemap tells Google about all your pages — without one, some pages (like event menus or seasonal specials) may never appear in search results.`);
    if (!hasRobots) bullets.push(`No robots.txt file found. This file guides search engines on how to crawl your site efficiently.`);
    if (issueCount > 0 && missingTitles === 0 && missingDesc === 0) bullets.push(`${issueCount} technical issue${issueCount === 1 ? '' : 's'} found that could be limiting your search visibility.`);
    if (issueCount === 0) bullets.push(`No technical SEO issues found — your site's foundation is solid for search engines.`);

    return { headline, bullets };
  }

  if (segment === 'tracking') {
    const hasGTM = site.tracking?.gtm?.present;
    const hasGA4 = site.tracking?.ga4?.present;
    const hasFBPixel = site.tracking?.facebookPixel?.present;
    const coverage = site.tracking?.events?.trackingCoverage;
    const coverageScore = coverage?.overallScore ?? 0;
    const gaps = site.tracking?.events?.trackingGaps || [];
    const criticalGaps = gaps.filter((g: any) => g.priority === 'critical').length;

    const headline = coverageScore >= 70
      ? `Your tracking setup captures most key customer actions. This data helps you understand what's working and what's not in your marketing.`
      : coverageScore >= 40
        ? `Your tracking setup is partially in place, but you're missing data on some important customer actions — meaning you can't fully measure which marketing efforts are driving reservations and orders.`
        : `Your website has significant gaps in tracking. Without proper tracking, you're spending money on marketing with no way to know what's actually bringing customers through the door.`;

    if (!hasGTM) bullets.push(`Google Tag Manager is not installed. GTM lets your marketing team add and update tracking without needing a developer for every change.`);
    if (!hasGA4) bullets.push(`Google Analytics (GA4) is not installed. Without it, you have no visibility into how many people visit your site, where they come from, or what pages they view.`);
    if (!hasFBPixel) bullets.push(`Facebook/Meta Pixel is not installed. If you run any Facebook or Instagram ads, you're unable to measure their effectiveness or retarget visitors who didn't convert.`);
    if (criticalGaps > 0) bullets.push(`${criticalGaps} critical tracking gap${criticalGaps === 1 ? '' : 's'}: you're not capturing data on key actions like reservation clicks or online order starts. This means you can't attribute revenue to your marketing spend.`);
    if (hasGTM && hasGA4 && coverageScore >= 70) bullets.push(`Core tracking tools are in place and covering key events — you have the data foundation to make informed marketing decisions.`);

    return { headline, bullets };
  }

  if (segment === 'architecture') {
    const archScore = site.architecture?.score ?? 0;
    const maxDepth = site.architecture?.structure?.maxDepth ?? 0;
    const orphanPages = site.architecture?.linkAnalysis?.orphanPages?.length ?? 0;
    const deadEndPages = site.architecture?.linkAnalysis?.deadEndPages?.length ?? 0;
    const avgLinks = site.architecture?.linkAnalysis?.avgLinksPerPage ?? 0;

    const headline = archScore >= 80
      ? `Your site structure is well-organized — search engines can easily find and index all your pages.`
      : archScore >= 50
        ? `Your site structure has some gaps that could be hiding pages from Google and making it harder for visitors to explore your full offerings.`
        : `Your site structure needs attention — important pages may be invisible to Google, and visitors are likely missing key content like your menu, events, and specials.`;

    if (maxDepth > 3) bullets.push(`Some pages are ${maxDepth} clicks deep from your homepage — visitors and Google may never find them.`);
    if (orphanPages > 0) bullets.push(`${orphanPages} page${orphanPages === 1 ? ' has' : 's have'} no links pointing to ${orphanPages === 1 ? 'it' : 'them'} — ${orphanPages === 1 ? "it's" : "they're"} invisible to search engines.`);
    if (deadEndPages > 0) bullets.push(`${deadEndPages} page${deadEndPages === 1 ? ' has' : 's have'} no links to other pages — visitors hit a wall and leave.`);
    if (avgLinks < 3 && avgLinks > 0) bullets.push(`Pages average only ${avgLinks} links each — better cross-linking keeps visitors exploring your menu, events, and specials.`);
    if (archScore >= 80 && orphanPages === 0 && deadEndPages === 0) bullets.push(`Your site is well-connected — search engines can easily find and index all your pages.`);

    return { headline, bullets };
  }

  return { headline: '', bullets: [] };
}

function CoverageBar({ label, count, total, issues }: { label: string; count: number; total: number; issues?: string }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const isGood = pct >= 80;
  const isMedium = pct >= 50 && pct < 80;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm">{label}</span>
        <div className="flex items-center gap-2">
          {issues && <span className="text-xs text-orange-400">{issues}</span>}
          <span className={`text-sm font-medium ${isGood ? 'text-green-500' : isMedium ? 'text-orange-400' : 'text-red-400'}`}>
            {count}/{total}
          </span>
        </div>
      </div>
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${isGood ? 'bg-green-500/70' : isMedium ? 'bg-orange-400/70' : 'bg-red-500/70'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ============================================
// SEO Metrics Panel with Filters
// ============================================

type SeoScoreFilter = 'all' | 'poor' | 'average' | 'good' | 'excellent';

function SeoMetricsPanel({ pages }: { pages: any[] }) {
  const [scoreFilter, setScoreFilter] = useState<SeoScoreFilter>('all');

  // Calculate aggregate stats
  const avgScore = pages.length > 0
    ? Math.round(pages.reduce((sum: number, p: any) => sum + (p.qualityAnalysis?.overallScore || 0), 0) / pages.length)
    : 0;
  const pagesWithIssues = pages.filter((p: any) =>
    (p.qualityAnalysis?.title?.issues?.length > 0) ||
    (p.qualityAnalysis?.description?.issues?.length > 0) ||
    (p.qualityAnalysis?.headings?.issues?.length > 0)
  ).length;
  const missingTitles = pages.filter((p: any) => !p.title).length;
  const missingDescriptions = pages.filter((p: any) => !p.metaDescription).length;

  // Score distribution for filter counts
  const scoreDistribution = {
    poor: pages.filter((p: any) => (p.qualityAnalysis?.overallScore || 0) < 50).length,
    average: pages.filter((p: any) => {
      const score = p.qualityAnalysis?.overallScore || 0;
      return score >= 50 && score < 70;
    }).length,
    good: pages.filter((p: any) => {
      const score = p.qualityAnalysis?.overallScore || 0;
      return score >= 70 && score < 90;
    }).length,
    excellent: pages.filter((p: any) => (p.qualityAnalysis?.overallScore || 0) >= 90).length,
  };

  // Filter pages based on score
  const filteredPages = pages.filter((page: any) => {
    const score = page.qualityAnalysis?.overallScore || 0;
    switch (scoreFilter) {
      case 'poor': return score < 50;
      case 'average': return score >= 50 && score < 70;
      case 'good': return score >= 70 && score < 90;
      case 'excellent': return score >= 90;
      default: return true;
    }
  });

  const filterOptions: { id: SeoScoreFilter; label: string; count: number; color: string }[] = [
    { id: 'all', label: 'All', count: pages.length, color: 'text-foreground' },
    { id: 'poor', label: '0-49', count: scoreDistribution.poor, color: 'text-red-400' },
    { id: 'average', label: '50-69', count: scoreDistribution.average, color: 'text-orange-400' },
    { id: 'good', label: '70-89', count: scoreDistribution.good, color: 'text-yellow-400' },
    { id: 'excellent', label: '90-100', count: scoreDistribution.excellent, color: 'text-green-400' },
  ];

  return (
    <div className="space-y-6">
      {/* Aggregate Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-secondary/30 rounded-lg p-4 text-center">
          <p className={`text-2xl font-light ${getScoreColor(avgScore)}`}>{avgScore}</p>
          <p className="text-xs text-muted-foreground">Avg Quality Score</p>
        </div>
        <div className="bg-secondary/30 rounded-lg p-4 text-center">
          <p className="text-2xl font-light text-orange-400">{pagesWithIssues}</p>
          <p className="text-xs text-muted-foreground">Pages with Issues</p>
        </div>
        <div className="bg-secondary/30 rounded-lg p-4 text-center">
          <p className={`text-2xl font-light ${missingTitles > 0 ? 'text-red-400' : 'text-green-400'}`}>{missingTitles}</p>
          <p className="text-xs text-muted-foreground">Missing Titles</p>
        </div>
        <div className="bg-secondary/30 rounded-lg p-4 text-center">
          <p className={`text-2xl font-light ${missingDescriptions > 0 ? 'text-red-400' : 'text-green-400'}`}>{missingDescriptions}</p>
          <p className="text-xs text-muted-foreground">Missing Descriptions</p>
        </div>
      </div>

      {/* Per-page expandable cards */}
      <div className="space-y-4">
        {/* Filter toolbar */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Click on any page to see detailed SEO quality analysis and element tracking
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Filter by SEO Score:</span>
            <div className="flex gap-1 bg-secondary/30 rounded-lg p-1">
              {filterOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setScoreFilter(option.id)}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5 ${
                    scoreFilter === option.id
                      ? 'bg-foreground/10 text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <span className={scoreFilter === option.id ? option.color : ''}>{option.label}</span>
                  <span className="text-muted-foreground/50">({option.count})</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Page cards */}
        <div className="space-y-2">
          {filteredPages.length > 0 ? (
            filteredPages.map((page: any, i: number) => (
              <PageDetailCard key={i} page={page} />
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No pages match the selected filter.
            </p>
          )}
        </div>

        {/* Filter summary */}
        {scoreFilter !== 'all' && (
          <p className="text-xs text-muted-foreground text-center">
            Showing {filteredPages.length} of {pages.length} pages
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================
// Local SEO Panel
// ============================================

function LocalSeoPanel({ localSeo }: { localSeo: any }) {
  return (
    <div className="space-y-6">
      {/* Local SEO Score */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Local SEO</h3>
          <p className="text-sm text-muted-foreground">
            How well optimized for local search (Google Maps, "near me" searches)
          </p>
        </div>
        <div className="text-right">
          <p className={`text-3xl font-light ${getScoreColor(localSeo.score)}`}>{localSeo.score}</p>
          <p className="text-xs text-muted-foreground">local SEO score</p>
        </div>
      </div>

      {/* Schema Status */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Code className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Structured Data (Schema)
          </h3>
        </div>
        <div className={`rounded-lg p-4 ${
          localSeo.localBusinessSchema.found
            ? 'bg-secondary/30'
            : 'bg-red-500/10 border border-red-500/20'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            {localSeo.localBusinessSchema.found ? (
              <CheckCircle2 className="h-4 w-4 text-green-500/70" />
            ) : (
              <XCircle className="h-4 w-4 text-red-500" />
            )}
            <span className="font-medium">
              {localSeo.localBusinessSchema.found
                ? `${localSeo.localBusinessSchema.type || 'LocalBusiness'} Schema Found`
                : 'No LocalBusiness Schema Found'}
            </span>
          </div>

          {localSeo.localBusinessSchema.found ? (
            <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
              <div className="space-y-2">
                <SchemaField label="Business Name" value={localSeo.localBusinessSchema.name} />
                <SchemaField label="Phone" value={localSeo.localBusinessSchema.telephone} />
                <SchemaField label="Cuisine" value={localSeo.localBusinessSchema.servesCuisine} />
                <SchemaField label="Price Range" value={localSeo.localBusinessSchema.priceRange} />
              </div>
              <div className="space-y-2">
                <SchemaField
                  label="Address"
                  value={localSeo.localBusinessSchema.address
                    ? `${localSeo.localBusinessSchema.address.street || ''}, ${localSeo.localBusinessSchema.address.city || ''}`
                    : null}
                />
                <SchemaField
                  label="Hours"
                  value={localSeo.localBusinessSchema.openingHours ? 'Present' : null}
                />
                <SchemaField
                  label="Rating"
                  value={localSeo.localBusinessSchema.aggregateRating
                    ? `${localSeo.localBusinessSchema.aggregateRating.ratingValue} (${localSeo.localBusinessSchema.aggregateRating.reviewCount} reviews)`
                    : null}
                />
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground mt-2">
              Add LocalBusiness or Restaurant schema to appear in Google's Knowledge Panel and rich search results.
            </p>
          )}
        </div>
      </div>

      {/* NAP Consistency */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            NAP Consistency (Name, Address, Phone)
          </h3>
        </div>
        <div className="bg-secondary/30 rounded-lg p-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              {localSeo.napConsistency.nameFound ? (
                <CheckCircle2 className="h-5 w-5 text-green-500/70 mx-auto" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500 mx-auto" />
              )}
              <p className="text-xs text-muted-foreground mt-1">Name</p>
            </div>
            <div className="text-center">
              {localSeo.napConsistency.addressFound ? (
                <CheckCircle2 className="h-5 w-5 text-green-500/70 mx-auto" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500 mx-auto" />
              )}
              <p className="text-xs text-muted-foreground mt-1">Address</p>
            </div>
            <div className="text-center">
              {localSeo.napConsistency.phoneFound ? (
                <CheckCircle2 className="h-5 w-5 text-green-500/70 mx-auto" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500 mx-auto" />
              )}
              <p className="text-xs text-muted-foreground mt-1">Phone</p>
            </div>
          </div>
          {localSeo.napConsistency.issues.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              {localSeo.napConsistency.issues.map((issue: string, i: number) => (
                <p key={i} className="text-xs text-orange-400 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {issue}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Local Features */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Store className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Local Features
          </h3>
        </div>
        <div className="bg-secondary/30 rounded-lg p-4 space-y-3">
          <CheckRow label="Google Maps Link" value={localSeo.hasGoogleMapsLink} />
          <CheckRow label="Directions Link" value={localSeo.hasDirectionsLink} />
          <CheckRow
            label={`Opening Hours (${localSeo.openingHoursFormat})`}
            value={localSeo.hasOpeningHours}
          />
          <CheckRow label="Menu Schema" value={localSeo.hasMenuSchema} />
          <CheckRow label="Reservation Links" value={localSeo.hasReservationLink} />
        </div>

        {localSeo.reservationPlatforms.length > 0 && (
          <div className="text-xs text-muted-foreground">
            Reservation platforms: {localSeo.reservationPlatforms.join(', ')}
          </div>
        )}
      </div>

      {/* Issues */}
      {localSeo.issues.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Local SEO Issues ({localSeo.issues.length})
            </h3>
          </div>
          <div className="space-y-2">
            {localSeo.issues.map((issue: any, i: number) => (
              <div
                key={i}
                className={`rounded-lg p-3 border ${
                  issue.severity === 'error' ? 'bg-red-500/10 border-red-500/20' :
                  issue.severity === 'warning' ? 'bg-orange-500/10 border-orange-500/20' :
                  'bg-blue-500/10 border-blue-500/20'
                }`}
              >
                <div className="flex items-start gap-2">
                  {issue.severity === 'error' ? (
                    <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  ) : issue.severity === 'warning' ? (
                    <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Info className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  )}
                  <p className="text-sm">{issue.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Tracking Tab
// ============================================

function TrackingTab({ site, activePanel, setActivePanel }: { site: any; activePanel: SubPanelId; setActivePanel: (id: SubPanelId) => void }) {
  const orderingPlatforms = site.tracking.orderingPlatforms || [];
  const hasOrderingPlatforms = orderingPlatforms.length > 0;
  const trackingCoverage = site.tracking.events?.trackingCoverage;
  const trackingGaps = site.tracking.events?.trackingGaps || [];
  const pages = site.seo?.pages || [];

  const subPanels = [
    { id: 'summary' as const, label: 'Summary' },
    { id: 'funnel' as const, label: 'Conversion Funnel' },
    { id: 'pages' as const, label: 'Page Elements' },
    ...(hasOrderingPlatforms ? [{ id: 'toast' as const, label: 'Ordering Platforms' }] : []),
  ];

  return (
    <div className="space-y-6">
      <SubPanelNav panels={subPanels} active={activePanel} onChange={setActivePanel} />

      {activePanel === 'summary' && (
        <div className="space-y-6">
          {/* Why This Matters - Tracking */}
          {(() => {
            const { headline, bullets } = generateWhyItMatters('tracking', site);
            return <WhyItMatters headline={headline} bullets={bullets} segment="tracking" />;
          })()}

          {/* What Success Looks Like - Tracking */}
          <WhatSuccessLooksLike segment="tracking" site={site} />

          {/* Tracking Coverage Overview */}
          {trackingCoverage && (
            <div className="bg-secondary/30 rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-medium">Event Tracking Coverage</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {trackingCoverage.overallScore >= 70 ? 'Good coverage of essential restaurant tracking events' :
                     trackingCoverage.overallScore >= 40 ? 'Some tracking in place but missing key events' :
                     'Critical tracking gaps — losing valuable conversion data'}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`text-3xl font-light ${getScoreColor(trackingCoverage.overallScore)}`}>
                    {trackingCoverage.overallScore}%
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Essential', tracked: trackingCoverage.essentialTracked, total: trackingCoverage.essentialTotal },
                  { label: 'Important', tracked: trackingCoverage.importantTracked, total: trackingCoverage.importantTotal },
                  { label: 'Recommended', tracked: trackingCoverage.recommendedTracked, total: trackingCoverage.recommendedTotal },
                ].map((tier) => {
                  const pct = tier.total > 0 ? Math.round((tier.tracked / tier.total) * 100) : 0;
                  return (
                    <div key={tier.label} className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{tier.label}</span>
                        <span className="font-medium">{tier.tracked}/{tier.total}</span>
                      </div>
                      <div className="w-full bg-secondary rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${
                            pct === 100 ? 'bg-green-500/70' :
                            pct >= 50 ? 'bg-yellow-500/70' :
                            'bg-red-500/70'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Platforms Detected */}
          <div className="grid md:grid-cols-3 gap-4">
            {/* GTM */}
            <TrackingCard
              title="Google Tag Manager"
              present={site.tracking.gtm.present}
              details={[
                { label: 'Container', value: site.tracking.gtm.containerId },
                { label: 'dataLayer', value: site.tracking.gtm.dataLayerFound ? 'Found' : 'Not found' },
              ]}
            />

            {/* GA4 */}
            <TrackingCard
              title="Google Analytics 4"
              present={site.tracking.ga4.present}
              details={[
                { label: 'ID', value: site.tracking.ga4.measurementId },
                { label: 'Via', value: site.tracking.ga4.implementationType },
              ]}
              critical
            />

            {/* FB Pixel */}
            <TrackingCard
              title="Facebook Pixel"
              present={site.tracking.facebookPixel.present}
              details={[
                { label: 'Pixel ID', value: site.tracking.facebookPixel.pixelId },
                { label: 'Events', value: site.tracking.facebookPixel.eventsDetected?.join(', ') || 'None' },
              ]}
            />
          </div>

          {/* Tracking Gaps */}
          {trackingGaps.length > 0 && (
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    What's Not Being Tracked
                  </h4>
                </div>
                <span className="text-xs text-muted-foreground">
                  {trackingGaps.length} gap{trackingGaps.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="space-y-2">
                {(['critical', 'high', 'medium', 'low'] as const).map((priority) => {
                  const gaps = trackingGaps.filter((g: any) => g.priority === priority);
                  if (gaps.length === 0) return null;

                  const priorityConfig = {
                    critical: { label: 'Critical', border: 'border-l-red-500/60', dot: 'bg-red-500' },
                    high: { label: 'High', border: 'border-l-orange-500/60', dot: 'bg-orange-500' },
                    medium: { label: 'Medium', border: 'border-l-yellow-500/60', dot: 'bg-yellow-500' },
                    low: { label: 'Nice to Have', border: 'border-l-blue-500/60', dot: 'bg-blue-500' },
                  };

                  const config = priorityConfig[priority];

                  return (
                    <div key={priority} className="space-y-1.5">
                      <div className="flex items-center gap-2 pt-2">
                        <div className={`h-2 w-2 rounded-full ${config.dot}`} />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {config.label} ({gaps.length})
                        </span>
                      </div>
                      {gaps.map((gap: any, i: number) => (
                        <TrackingGapRow key={i} gap={gap} borderClass={config.border} />
                      ))}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

        </div>
      )}

      {activePanel === 'funnel' && (
        <ConversionFunnel tracking={site.tracking} />
      )}

      {activePanel === 'pages' && (() => {
        const pagesWithElements = pages.filter((p: any) => p.trackingAnalysis?.elements?.length > 0);
        return (
          <div className="space-y-4">
            {/* Coverage overview */}
            <ElementTrackingOverview pages={pagesWithElements} />

            {/* How elements are tracked */}
            <TrackingMethodsChart pages={pagesWithElements} />

            {/* Untracked elements needing attention */}
            <UntrackedElementsSummary pages={pagesWithElements} />

            {/* Page-by-page detail */}
            {pagesWithElements.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider pt-2">
                  Page-by-Page Detail
                </h3>
                {pagesWithElements.map((page: any, i: number) => (
                  <PageTrackingCard key={i} page={page} />
                ))}
              </div>
            )}

            {pagesWithElements.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No trackable elements found across crawled pages.
              </p>
            )}
          </div>
        );
      })()}


      {activePanel === 'toast' && (
        <div className="space-y-6">
          {/* Ordering Platforms Summary */}
          {orderingPlatforms.length > 0 && (
            <>
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Detected Ordering & Reservation Platforms
                </h3>
                <p className="text-xs text-muted-foreground">
                  These third-party platforms handle orders and reservations. Click tracking on these links is essential for measuring marketing ROI.
                </p>
              </div>

              <div className="grid gap-4">
                {orderingPlatforms.map((platform: any, i: number) => (
                  <div
                    key={i}
                    className={`rounded-lg p-4 border ${
                      platform.isTracked
                        ? 'bg-green-500/10 border-green-500/20'
                        : 'bg-red-500/10 border-red-500/20'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className={`font-medium ${platform.isTracked ? 'text-green-400' : 'text-red-400'}`}>
                        {platform.platformName}
                      </h4>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{platform.linkCount} link(s)</span>
                        {platform.isTracked ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {platform.isTracked
                        ? 'Click events on these links are being captured.'
                        : 'WARNING: These links are NOT being tracked - losing conversion data.'}
                    </p>
                    <div className="space-y-1">
                      {platform.urls.slice(0, 3).map((url: string, j: number) => (
                        <a
                          key={j}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-mono text-muted-foreground hover:text-foreground flex items-center gap-1 truncate"
                        >
                          {url}
                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </a>
                      ))}
                      {platform.urls.length > 3 && (
                        <p className="text-xs text-muted-foreground">+{platform.urls.length - 3} more</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Legacy Toast Integration Section */}
          {site.tracking.events?.toastIntegration?.hasToastLinks && orderingPlatforms.length === 0 && (
            <>
              <div className={`rounded-lg p-4 ${
                site.tracking.events.toastIntegration.toastLinksTracked
                  ? 'bg-green-500/10 border border-green-500/20'
                  : 'bg-red-500/10 border border-red-500/20'
              }`}>
                <h4 className={`font-medium ${site.tracking.events.toastIntegration.toastLinksTracked ? 'text-green-400' : 'text-red-400'}`}>
                  {site.tracking.events.toastIntegration.toastLinksTracked
                    ? 'Toast order links are being tracked'
                    : 'WARNING: Toast links are NOT being tracked'}
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  {site.tracking.events.toastIntegration.toastLinksTracked
                    ? 'Click events on Toast ordering links are being captured.'
                    : 'You are losing conversion data. Customers clicking to order are not being tracked.'}
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-medium">Toast Links Found ({site.tracking.events.toastIntegration.toastUrls.length})</h4>
                {site.tracking.events.toastIntegration.toastUrls.map((url: string, i: number) => (
                  <div key={i} className="bg-secondary/30 rounded p-3">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm font-mono text-muted-foreground hover:text-foreground flex items-center gap-2">
                      {url}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                ))}
              </div>
            </>
          )}

          {orderingPlatforms.length === 0 && !site.tracking.events?.toastIntegration?.hasToastLinks && (
            <div className="text-center py-8 text-muted-foreground">
              No ordering or reservation platform links detected on this site.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TrackingGapRow({ gap, borderClass }: { gap: any; borderClass: string }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`bg-secondary/20 rounded-lg border-l-2 ${borderClass} cursor-pointer transition-colors hover:bg-secondary/30`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center justify-between px-4 py-2.5">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-sm font-medium truncate">{gap.event}</span>
          <span className="text-xs text-muted-foreground px-1.5 py-0.5 bg-secondary rounded flex-shrink-0">
            {gap.category}
          </span>
        </div>
        <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`} />
      </div>
      {expanded && (
        <div className="px-4 pb-3 space-y-1.5 border-t border-border/30 pt-2">
          <p className="text-xs text-muted-foreground">{gap.description}</p>
          {gap.whyItMatters && (
            <p className="text-xs text-muted-foreground/80">
              <span className="font-medium text-muted-foreground">Why it matters:</span> {gap.whyItMatters}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Executive Summary Components
// ============================================

function ExecutiveScoreCard({
  label,
  score,
  percentile,
}: {
  label: string;
  score: number;
  percentile: number;
}) {
  const description = getPercentileDescription(percentile);
  const isGood = percentile >= 75;
  const isAverage = percentile >= 50 && percentile < 75;

  return (
    <div className="bg-secondary/30 rounded-lg p-4 text-center">
      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-3xl font-light ${getScoreColor(score)}`}>{score}</p>
      <div className="flex items-center justify-center gap-1.5 mt-2">
        {isGood ? (
          <TrendingUp className="h-3 w-3 text-green-500/70" />
        ) : isAverage ? (
          <Gauge className="h-3 w-3 text-orange-400/70" />
        ) : (
          <TrendingDown className="h-3 w-3 text-red-500/70" />
        )}
        <span className={`text-xs ${getPercentileColor(percentile)}`}>
          {description}
        </span>
      </div>
    </div>
  );
}

function LoadTimeCard({
  icon,
  label,
  time,
  rating,
  benchmark,
}: {
  icon: React.ReactNode;
  label: string;
  time: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  benchmark: string;
}) {
  const colors = {
    good: 'bg-green-500/10 border-green-500/20 text-green-500/80',
    'needs-improvement': 'bg-orange-500/10 border-orange-500/20 text-orange-500/80',
    poor: 'bg-red-500/10 border-red-500/20 text-red-500/80',
  };

  const ratingLabels = {
    good: 'Fast',
    'needs-improvement': 'Moderate',
    poor: 'Slow',
  };

  return (
    <div className={`rounded-lg border p-4 ${colors[rating]}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="text-muted-foreground">{icon}</div>
          <div>
            <p className="text-sm font-medium text-foreground">{label}</p>
            <p className="text-xs text-muted-foreground">{benchmark}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-light">{time.toFixed(1)}s</p>
          <p className={`text-xs font-medium ${colors[rating].split(' ')[2]}`}>
            {ratingLabels[rating]}
          </p>
        </div>
      </div>
    </div>
  );
}

function ScoreBreakdownCard({
  title,
  score,
  description,
  factors,
  showMetrics = false,
}: {
  title: string;
  score: number;
  description: string;
  factors: Array<{
    name: string;
    impact: 'high' | 'medium' | 'low';
    status: 'pass' | 'warning' | 'fail';
    detail: string;
    value?: string;
    target?: string;
  }>;
  showMetrics?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const impactLabels = {
    high: 'High Impact',
    medium: 'Medium Impact',
    low: 'Low Impact',
  };

  const impactColors = {
    high: 'text-foreground',
    medium: 'text-muted-foreground',
    low: 'text-muted-foreground/70',
  };

  const statusIcons = {
    pass: <CheckCircle2 className="h-4 w-4 text-green-500/70" />,
    warning: <AlertTriangle className="h-4 w-4 text-orange-500/70" />,
    fail: <XCircle className="h-4 w-4 text-red-500/70" />,
  };

  const statusColors = {
    pass: 'text-green-500/80',
    warning: 'text-orange-500/80',
    fail: 'text-red-500/80',
  };

  const statusLabels = {
    pass: 'Good',
    warning: 'Needs Work',
    fail: 'Poor',
  };

  const passCount = factors.filter(f => f.status === 'pass').length;
  const totalCount = factors.length;

  return (
    <div className="bg-secondary/20 rounded-lg border border-border/50 overflow-hidden">
      {/* Header - Always Visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={`text-3xl font-light ${getScoreColor(score)}`}>{score}</div>
          <div className="text-left">
            <p className="font-medium">{title}</p>
            <p className="text-xs text-muted-foreground">{passCount}/{totalCount} checks passing</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex -space-x-1">
            {factors.slice(0, 6).map((factor, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full ${
                  factor.status === 'pass' ? 'bg-green-500/60' :
                  factor.status === 'warning' ? 'bg-orange-500/60' :
                  'bg-red-500/60'
                }`}
              />
            ))}
          </div>
          <Info className="h-4 w-4 text-muted-foreground" />
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-border/50 p-4 space-y-4">
          <p className="text-sm text-muted-foreground">{description}</p>

          {showMetrics ? (
            /* Metrics Table View */
            <div className="overflow-hidden rounded-lg border border-border/30">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary/30 border-b border-border/30">
                    <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider">Metric</th>
                    <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider">Value</th>
                    <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider">Rating</th>
                    <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider">Target</th>
                  </tr>
                </thead>
                <tbody>
                  {factors.map((factor, i) => (
                    <tr key={i} className="border-b border-border/20 last:border-0">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {statusIcons[factor.status]}
                          <div>
                            <p className="font-medium text-sm">{factor.name}</p>
                            <p className="text-xs text-muted-foreground">{factor.detail}</p>
                          </div>
                        </div>
                      </td>
                      <td className={`p-3 font-mono text-sm ${statusColors[factor.status]}`}>
                        {factor.value || '—'}
                      </td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-1 rounded ${
                          factor.status === 'pass' ? 'bg-green-500/10 text-green-500/80' :
                          factor.status === 'warning' ? 'bg-orange-500/10 text-orange-500/80' :
                          'bg-red-500/10 text-red-500/80'
                        }`}>
                          {statusLabels[factor.status]}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground font-mono">
                        {factor.target || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* Standard List View */
            <div className="space-y-2">
              {factors.map((factor, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-2 border-b border-border/30 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    {statusIcons[factor.status]}
                    <div>
                      <p className={`text-sm ${impactColors[factor.impact]}`}>{factor.name}</p>
                      <p className="text-xs text-muted-foreground">{factor.detail}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    factor.impact === 'high' ? 'bg-secondary text-foreground' :
                    'bg-secondary/50 text-muted-foreground'
                  }`}>
                    {impactLabels[factor.impact]}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
